import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getProject, updateProject, deleteRoom as apiDeleteRoom, geocodeProject } from '../api/projects'
import type { Project, Room, ProjectUpdate } from '../types'
import { Layout } from '../components/Layout'
import { Button } from '../components/Button'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { Modal } from '../components/Modal'
import { Input } from '../components/Input'
import { RoomCard } from './RoomCard'
import { RoomEditor } from './RoomEditor'
import { GenerateButton } from '../documents/GenerateButton'
import { GeocoordinatesModal } from './GeocoordinatesModal'
import { buildImageUrl } from '../utils/buildImageUrl'
import { usePlanPreview } from './usePlanPreview'
import styles from './ProjectDetailPage.module.css'


const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  validated: 'Validado',
  generated: 'Generado',
}

const STATUS_CLASS: Record<string, string> = {
  draft: 'draft',
  validated: 'validated',
  generated: 'generated',
}

const REGIONES = [
  'Arica y Parinacota', 'Tarapacá', 'Antofagasta', 'Atacama', 'Coquimbo',
  'Valparaíso', 'Metropolitana de Santiago', "O'Higgins", 'Maule',
  'Ñuble', 'Biobío', 'La Araucanía', 'Los Ríos', 'Los Lagos',
  'Aysén', 'Magallanes',
]

function PlanThumbnailContent({ project }: { project: Project }) {
  const hasRooms = (project.rooms?.length ?? 0) > 0
  const { url: previewUrl, loading } = usePlanPreview(project.id, hasRooms, project.updated_at)

  if (loading) {
    return <div className={styles.planThumbnailSkeleton} />
  }

  if (previewUrl) {
    return (
      <>
        <img
          src={previewUrl}
          alt="Plano del proyecto"
          className={styles.planThumbnailImg}
        />
        <div className={styles.planThumbnailOverlay}>Abrir workspace</div>
      </>
    )
  }

  if (project.image_path) {
    return (
      <>
        <img
          src={buildImageUrl(project.image_path)}
          alt="Plano del proyecto"
          className={styles.planThumbnailImg}
        />
        <div className={styles.planThumbnailOverlay}>Abrir workspace</div>
      </>
    )
  }
  return (
    <div className={styles.planPlaceholder}>
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <rect x="6" y="10" width="36" height="28" rx="3" stroke="currentColor" strokeWidth="2" fill="none"/>
        <path d="M6 22h36" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M20 22v16" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M14 10v12" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M30 22v6" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
      <p className={styles.planPlaceholderText}>Sin plano</p>
      <p className={styles.planPlaceholderText} style={{ fontSize: '12px' }}>Toca para subir</p>
    </div>
  )
}

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const [editOpen, setEditOpen] = useState(false)
  const [editForm, setEditForm] = useState<Partial<ProjectUpdate>>({})
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState<string | undefined>()

  const [roomEditorOpen, setRoomEditorOpen] = useState(false)
  const [editingRoom, setEditingRoom] = useState<Room | null>(null)

  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false)
  const [geocodeError, setGeocodeError] = useState<string | undefined>()
  const [showGeoModal, setShowGeoModal] = useState(false)

  const load = useCallback(async () => {
    if (!id) return
    setLoading(true)
    try {
      const data = await getProject(id)
      setProject(data)
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } }
      if (axiosErr?.response?.status === 404) {
        setNotFound(true)
      }
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { load() }, [load])

  const openEdit = () => {
    if (!project) return
    setEditForm({
      name: project.name ?? '',
      address: project.address ?? '',
      commune: project.commune ?? '',
      region: project.region ?? '',
      square_meters: project.square_meters ?? undefined,
      dwelling_type: project.dwelling_type ?? '',
      installation_type: project.installation_type ?? '',
      normative_version: project.normative_version,
    })
    setEditError(undefined)
    setEditOpen(true)
  }

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !editForm.name?.trim()) {
      setEditError('El nombre es obligatorio.')
      return
    }
    setEditLoading(true)
    try {
      const updated = await updateProject(id, editForm)
      setProject(updated)
      setEditOpen(false)
    } catch {
      setEditError('No pudimos guardar los cambios. Intenta nuevamente.')
    } finally {
      setEditLoading(false)
    }
  }

  const setEdit = (field: string) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => setEditForm((prev) => ({ ...prev, [field]: e.target.value }))

  const handleRoomSave = (saved: Room) => {
    setProject((prev) => {
      if (!prev) return prev
      const rooms = prev.rooms ?? []
      const exists = rooms.find((r) => r.id === saved.id)
      return {
        ...prev,
        rooms: exists
          ? rooms.map((r) => (r.id === saved.id ? saved : r))
          : [...rooms, saved],
      }
    })
    setRoomEditorOpen(false)
    setEditingRoom(null)
  }

  const handleRoomDelete = async (room: Room) => {
    if (!id) return
    try {
      await apiDeleteRoom(id, room.id)
      setProject((prev) =>
        prev ? { ...prev, rooms: (prev.rooms ?? []).filter((r) => r.id !== room.id) } : prev
      )
    } catch {
      // silently ignore
    }
  }

  const openRoomEditor = (room: Room | null) => {
    setEditingRoom(room)
    setRoomEditorOpen(true)
  }

  const handleGeocode = async () => {
    if (!id) return
    setIsGeocodingLoading(true)
    setGeocodeError(undefined)
    try {
      await geocodeProject(id)
      await load()
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message ?? ''
      if (msg.includes('NO_MAPS_API_KEY')) {
        setGeocodeError('La geocodificación automática no está disponible. Ingresa las coordenadas manualmente.')
      } else if (msg.includes('GEOCODE_NOT_FOUND')) {
        setGeocodeError('No se pudo encontrar la dirección. Ingresa las coordenadas manualmente.')
      } else {
        setGeocodeError('Error al geocodificar. Intenta ingresar las coordenadas manualmente.')
      }
    } finally {
      setIsGeocodingLoading(false)
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className={styles.centered}>
          <LoadingSpinner size="lg" message="Cargando proyecto..." />
        </div>
      </Layout>
    )
  }

  if (notFound || !project) {
    return (
      <Layout>
        <div className={styles.notFound}>
          <p className={styles.notFoundText}>Proyecto no encontrado.</p>
          <Button onClick={() => navigate('/')}>Volver al inicio</Button>
        </div>
      </Layout>
    )
  }

  const statusKey = project.status?.toLowerCase() ?? 'draft'
  const rooms = project.rooms ?? []

  return (
    <Layout>
      <div className={styles.page}>

        {/* Top bar sticky */}
        <div className={styles.topBar}>
          <button className={styles.back} onClick={() => navigate('/')} aria-label="Volver">
            ← Volver
          </button>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{project.name}</h1>
            <span className={`${styles.badge} ${styles[STATUS_CLASS[statusKey] ?? 'draft']}`}>
              {STATUS_LABELS[statusKey] ?? project.status}
            </span>
            <Button variant="secondary" onClick={openEdit}>
              Editar
            </Button>
          </div>
        </div>

        {/* Thumbnail mobile — before columns */}
        <button
          className={styles.planThumbnailMobile}
          onClick={() => navigate(`/projects/${id}/workspace`)}
          aria-label="Abrir workspace del plano"
        >
          <PlanThumbnailContent project={project} />
          <div className={styles.planThumbnailOverlay}>Abrir workspace ↗</div>
        </button>

        {/* Main layout */}
        <div className={styles.layout}>

          {/* Left column */}
          <div className={styles.leftCol}>

            {/* Datos del proyecto */}
            <div className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Datos del proyecto</h2>
              </div>
              <div className={styles.sectionBody}>
                <dl className={styles.grid}>
                  {project.address && (
                    <><dt className={styles.dt}>Dirección</dt><dd className={styles.dd}>{project.address}</dd></>
                  )}
                  {project.commune && (
                    <><dt className={styles.dt}>Comuna</dt><dd className={styles.dd}>{project.commune}</dd></>
                  )}
                  {project.region && (
                    <><dt className={styles.dt}>Región</dt><dd className={styles.dd}>{project.region}</dd></>
                  )}
                  {project.square_meters && (
                    <><dt className={styles.dt}>Superficie</dt><dd className={styles.dd}>{project.square_meters} m²</dd></>
                  )}
                  {project.dwelling_type && (
                    <><dt className={styles.dt}>Tipo vivienda</dt><dd className={styles.dd}>{project.dwelling_type}</dd></>
                  )}
                  {project.installation_type && (
                    <><dt className={styles.dt}>Instalación</dt><dd className={styles.dd}>{project.installation_type}</dd></>
                  )}
                  {project.normative_version && (
                    <><dt className={styles.dt}>Normativa</dt><dd className={styles.dd}>{project.normative_version === 'ric_2019' ? 'DS 08/2019 RIC' : project.normative_version}</dd></>
                  )}
                </dl>
              </div>
            </div>

            {/* Coordenadas */}
            {project.latitude === null || project.latitude === undefined ? (
              <div className={styles.geoWarning}>
                <div className={styles.geoWarningHeader}>
                  <span>⚠️</span>
                  <strong>Coordenadas geográficas requeridas</strong>
                </div>
                <p>La SEC exige las coordenadas del proyecto en el plano (RIC N°18 6.3.5). Sin esto el proyecto puede ser rechazado.</p>
                {geocodeError && <p className={styles.geoError}>{geocodeError}</p>}
                <div className={styles.geoActions}>
                  <Button variant="primary" loading={isGeocodingLoading} onClick={handleGeocode}>
                    {isGeocodingLoading ? 'Geocodificando...' : 'Obtener automáticamente'}
                  </Button>
                  <Button variant="secondary" onClick={() => setShowGeoModal(true)}>
                    Ingresar manualmente
                  </Button>
                </div>
              </div>
            ) : (
              <div className={styles.geoSuccess}>
                <div className={styles.geoSuccessHeader}>
                  <span>✅</span>
                  <strong>Coordenadas registradas</strong>
                </div>
                <p>Lat: {project.latitude.toFixed(6)}°  |  Lon: {project.longitude!.toFixed(6)}°</p>
                {project.map_image_path && (
                  <img src={project.map_image_path} alt="Mapa del proyecto" className={styles.mapThumbnail} />
                )}
                <Button variant="secondary" onClick={() => setShowGeoModal(true)}>Actualizar</Button>
              </div>
            )}

            {/* Recintos */}
            <div className={styles.sectionCard}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Recintos</h2>
                {rooms.length > 0 && (
                  <Button onClick={() => openRoomEditor(null)}>+ Agregar</Button>
                )}
              </div>
              {rooms.length === 0 ? (
                <div className={styles.emptyRooms}>
                  <p className={styles.emptyText}>Agrega los recintos de la instalación para continuar</p>
                  <Button onClick={() => openRoomEditor(null)} fullWidth>+ Agregar primer recinto</Button>
                </div>
              ) : (
                <div className={styles.roomList}>
                  {rooms.map((room) => (
                    <RoomCard
                      key={room.id}
                      room={room}
                      onEdit={(r) => openRoomEditor(r)}
                      onDelete={handleRoomDelete}
                    />
                  ))}
                  <Button variant="secondary" onClick={() => openRoomEditor(null)} fullWidth>+ Agregar recinto</Button>
                </div>
              )}
            </div>
          </div>

          {/* Right column (desktop) */}
          <div className={styles.rightCol}>
            {/* Thumbnail desktop */}
            <button
              className={styles.planThumbnail}
              onClick={() => navigate(`/projects/${id}/workspace`)}
              aria-label="Abrir workspace del plano"
            >
              <PlanThumbnailContent project={project} />
              <div className={styles.planThumbnailOverlay}>Abrir workspace ↗</div>
            </button>

            {/* Generate card */}
            {rooms.length > 0 && (
              <div className={styles.generateCard}>
                <h2 className={styles.generateTitle}>Generar set TE1</h2>
                <p className={styles.generateDesc}>
                  13 documentos conforme a DS 08/2019 RIC
                </p>
                <div className={styles.generateActions}>
                  {statusKey === 'generated' && (
                    <>
                      <p className={styles.generatedOk}>✓ Documentos generados</p>
                      <Button onClick={() => navigate(`/projects/${id}/documents`)} fullWidth>
                        Ver documentos
                      </Button>
                    </>
                  )}
                  <GenerateButton
                    projectId={id!}
                    onSuccess={() => {
                      setProject((prev) => prev ? { ...prev, status: 'generated' } : prev)
                      navigate(`/projects/${id}/documents`)
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sticky CTA mobile — generar TE1 */}
        {rooms.length > 0 && (
          <div className={styles.stickyCta}>
            {statusKey === 'generated' ? (
              <Button onClick={() => navigate(`/projects/${id}/documents`)} fullWidth>
                Ver documentos generados
              </Button>
            ) : (
              <GenerateButton
                projectId={id!}
                onSuccess={() => {
                  setProject((prev) => prev ? { ...prev, status: 'generated' } : prev)
                  navigate(`/projects/${id}/documents`)
                }}
              />
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <Modal isOpen={editOpen} onClose={() => setEditOpen(false)} title="Editar proyecto">
        <form onSubmit={handleEditSave} className={styles.editForm} noValidate>
          <Input
            label="Nombre del proyecto"
            name="editName"
            value={String(editForm.name ?? '')}
            onChange={setEdit('name')}
            error={editError}
            required
          />
          <Input
            label="Dirección"
            name="editAddress"
            value={String(editForm.address ?? '')}
            onChange={setEdit('address')}
          />
          <Input
            label="Comuna"
            name="editCommune"
            value={String(editForm.commune ?? '')}
            onChange={setEdit('commune')}
          />
          <div className={styles.editField}>
            <label className={styles.editLabel}>Región</label>
            <select className={styles.editSelect} value={String(editForm.region ?? '')} onChange={setEdit('region')}>
              <option value="">Selecciona una región</option>
              {REGIONES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <div className={styles.editActions}>
            <Button type="submit" loading={editLoading} fullWidth>Guardar cambios</Button>
            <Button variant="secondary" onClick={() => setEditOpen(false)} fullWidth>Cancelar</Button>
          </div>
        </form>
      </Modal>

      {roomEditorOpen && id && (
        <RoomEditor
          projectId={id}
          room={editingRoom}
          onSave={handleRoomSave}
          onClose={() => { setRoomEditorOpen(false); setEditingRoom(null) }}
        />
      )}

      {showGeoModal && id && (
        <GeocoordinatesModal
          projectId={id}
          onClose={() => setShowGeoModal(false)}
          onSaved={async () => {
            setShowGeoModal(false)
            await load()
          }}
        />
      )}
    </Layout>
  )
}
